import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { SrIcon } from "@/components/ui/SrIcon";
import { cn } from "@/lib/utils";
import {
  useSyncCoverage,
  TRACKED_SYNC_TYPES,
  type SyncCoverage,
  type SyncHealthStatus,
  type SyncTriggerSource,
} from "@/hooks/use-sync-coverage";
import { SyncLogTable } from "./SyncLogTable";

const HEALTH_STYLES: Record<SyncHealthStatus, { dot: string; pill: string; label: string }> = {
  healthy: { dot: "bg-emerald-500", pill: "bg-emerald-100 text-emerald-700", label: "OK" },
  stale: { dot: "bg-amber-500", pill: "bg-amber-100 text-amber-700", label: "Sliten" },
  warning: { dot: "bg-amber-500", pill: "bg-amber-100 text-amber-700", label: "Varsel" },
  critical: { dot: "bg-red-500", pill: "bg-red-100 text-red-700", label: "Feilet" },
  never_run: { dot: "bg-muted-foreground/40", pill: "bg-muted text-muted-foreground", label: "Aldri kjørt" },
};

const TRIGGER_LABELS: Record<SyncTriggerSource, string> = {
  cron: "Cron",
  manual: "Manuell",
  backfill: "Backfill",
  unknown: "—",
};

/**
 * Importdekning — viser status og dekningsintervall per (source, entity_type)
 * for PowerOffice- og Monday-syncer. Driver Eriks "Har vi alle bilag?"-spørsmål.
 *
 * Komponenten er superadmin-gated av forelder (DriftSettings).
 */
export function ImportCoverageSection() {
  const { data: coverages = [], isLoading } = useSyncCoverage();

  return (
    <Card>
      <CardContent className="p-6">
        <div className="flex items-center gap-2 mb-1">
          <SrIcon name="activity" className="h-4 w-4 text-muted-foreground" />
          <h2 className="font-display text-base">Importdekning</h2>
        </div>
        <p className="text-xs text-muted-foreground mb-4">
          Status og dekning for synker fra PowerOffice og Monday.
        </p>

        {isLoading ? (
          <p className="text-sm text-muted-foreground">Henter…</p>
        ) : (
          <div className="space-y-3">
            {coverages.map((c) => (
              <CoverageRow key={`${c.source}-${c.entity_type}`} coverage={c} />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function CoverageRow({ coverage }: { coverage: SyncCoverage }) {
  const [historyOpen, setHistoryOpen] = useState(false);
  const label =
    TRACKED_SYNC_TYPES.find(
      (t) => t.source === coverage.source && t.entity_type === coverage.entity_type,
    )?.label ?? `${coverage.source}/${coverage.entity_type}`;

  const health = HEALTH_STYLES[coverage.health_status];

  return (
    <div className="border border-border/50 rounded-lg overflow-hidden">
      <div className="p-3 bg-card/50">
        {/* Tittelrad */}
        <div className="flex items-center gap-3 mb-1">
          <span className={cn("w-2 h-2 rounded-full shrink-0", health.dot)} />
          <span className="font-medium text-sm">{label}</span>
          <span
            className={cn(
              "text-[10px] font-medium px-1.5 py-0.5 rounded",
              health.pill,
            )}
          >
            {health.label}
          </span>
          {coverage.last_triggered_by && coverage.last_triggered_by !== "unknown" && (
            <span className="text-[10px] text-muted-foreground">
              · {TRIGGER_LABELS[coverage.last_triggered_by]}
            </span>
          )}
          <div className="flex-1" />
          <button
            onClick={() => setHistoryOpen((v) => !v)}
            className="text-[11px] text-muted-foreground hover:text-primary inline-flex items-center gap-1"
            aria-expanded={historyOpen}
          >
            <SrIcon
              name={historyOpen ? "chevron-down" : "chevron-right"}
              className="w-3 h-3"
            />
            {historyOpen ? "Skjul historikk" : "Vis historikk"}
          </button>
        </div>

        {/* Dekningstekst */}
        <div className="text-xs text-muted-foreground space-y-0.5 ml-5">
          {coverage.coverage_from && coverage.coverage_to ? (
            <div>
              Dekket: <span className="text-foreground tabular-nums">{coverage.coverage_from}</span> →{" "}
              <span className="text-foreground tabular-nums">{coverage.coverage_to}</span>
              {coverage.projects_in_last_run && (
                <span className="text-muted-foreground/70"> · {coverage.projects_in_last_run.length} elementer i siste kjøring</span>
              )}
            </div>
          ) : coverage.health_status === "never_run" ? (
            <div className="italic">Aldri kjørt</div>
          ) : (
            <div className="italic text-muted-foreground/70">Dekning ukjent (mangler metadata på eldre rader)</div>
          )}

          {coverage.last_run_at && (
            <div>
              Sist kjørt: <span className="text-foreground">{relativeTime(coverage.last_run_at)}</span>
              {coverage.last_records_synced != null && (
                <span> · {coverage.last_records_synced} records</span>
              )}
              {coverage.last_status === "error" && coverage.last_error && (
                <span className="text-destructive ml-1">· {coverage.last_error}</span>
              )}
            </div>
          )}
        </div>

        {/* Hull-varsler */}
        {coverage.gap_warnings.length > 0 && (
          <div className="mt-2 ml-5 space-y-1">
            {coverage.gap_warnings.map((w, i) => (
              <div
                key={i}
                className={cn(
                  "text-xs px-2 py-1 rounded border-l-2",
                  w.kind === "cron_stalled"
                    ? "border-red-400 bg-red-50 text-red-800"
                    : "border-amber-400 bg-amber-50 text-amber-900",
                )}
              >
                <SrIcon
                  name={w.kind === "cron_stalled" ? "alert-octagon" : "alert-triangle"}
                  className="w-3 h-3 inline mr-1"
                />
                {w.message}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Historikk */}
      {historyOpen && (
        <div className="border-t bg-muted/20 p-3">
          <SyncLogTable
            source={coverage.source}
            entity_type={coverage.entity_type}
            limit={20}
          />
        </div>
      )}
    </div>
  );
}

/**
 * Liten lokal hjelpefunksjon — uttrykker tidsstempel som "5 min siden",
 * "3 timer siden", "2 dager siden". Holder oss inne i fil siden den ikke
 * brukes andre steder.
 */
function relativeTime(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000) return "akkurat nå";
  const minutes = Math.floor(ms / 60_000);
  if (minutes < 60) return `${minutes} min siden`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} ${hours === 1 ? "time" : "timer"} siden`;
  const days = Math.floor(hours / 24);
  return `${days} ${days === 1 ? "dag" : "dager"} siden`;
}
